import { Scan, ShareNetworkIcon } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { partition } from "lodash";
import { useParams } from "react-router";
import { toast } from "sonner";

import type { SafetyPassportCertification } from "@miter/types/contracts";
import { formatDateString } from "@miter/utils/date-utils";
import { Accordion, Button, MiterIcon, Card, CardContent, Loader } from "ui2";

import CertificationAccordionItem from "safety/components/CertificationAccordionItem";
import { PublicSafetyPageContainer, PublicSafetyStatusMessage } from "safety/components/PublicSafetyPage";
import TeamMemberInfo from "safety/components/TeamMemberInfo";
import { MiterAPI, PassportNotFoundError } from "safety/miter";

const DATE_FORMAT = "MMMM d, yyyy";

type CertificationSectionProps = {
  title: string;
  certifications: SafetyPassportCertification[];
  emptyLabel: string;
};

const CertificationSection = ({
  title,
  certifications,
  emptyLabel,
}: CertificationSectionProps): React.JSX.Element => (
  <>
    <div className="tw:font-denim tw:mb-2 tw:font-bold tw:m-0 tw:text-midnight/70">
      {title} · {certifications.length}
    </div>
    {certifications.length > 0 ? (
      <Accordion type="multiple">
        {certifications.map((cert) => (
          <CertificationAccordionItem key={cert.certification_type_id} cert={cert} />
        ))}
      </Accordion>
    ) : (
      <Card>
        <CardContent>{emptyLabel}</CardContent>
      </Card>
    )}
  </>
);

const PassportPage = (): React.JSX.Element => {
  const { token } = useParams<{ token: string }>();

  const {
    data: passport,
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: ["safety-passport", token],
    queryFn: () => MiterAPI.safety.passports.retrieve(token!),
    enabled: !!token,
    retry: false,
  });

  const copyPassportLink = async (): Promise<void> => {
    await navigator.clipboard.writeText(window.location.href);
    toast.success("Copied passport link!");
  };

  if (isLoading) {
    return (
      <PublicSafetyPageContainer>
        <Loader className="tw:flex-1" size="lg" />
      </PublicSafetyPageContainer>
    );
  }

  if (!token || error instanceof PassportNotFoundError) {
    return (
      <PublicSafetyPageContainer>
        <PublicSafetyStatusMessage
          title="Safety Passport not found"
          description="This safety passport link is invalid or is no longer available. Double-check the link, or ask the team member to share it again."
        />
      </PublicSafetyPageContainer>
    );
  }

  if (!passport) {
    return (
      <PublicSafetyPageContainer>
        <PublicSafetyStatusMessage
          title="Unable to load Safety Passport"
          description="Something went wrong while loading this safety passport. Check your connection and try again."
          action={
            <Button variant="outline" onClick={() => void refetch()} disabled={isRefetching}>
              Try again
            </Button>
          }
        />
      </PublicSafetyPageContainer>
    );
  }

  const [activeCertifications, expiredCertifications] = partition(
    passport.certifications,
    (cert) => cert.status === "active"
  );

  return (
    <PublicSafetyPageContainer>
      <div className="tw:p-3 tw:flex tw:flex-row tw:items-center tw:gap-3 tw:border-b tw:border-stroke tw:shrink-0">
        <MiterIcon />
        <div className="tw:text-lg tw:font-denim">Miter Safety Passport</div>
        <Button className="tw:ml-auto" size="icon" variant="ghost" onClick={copyPassportLink}>
          <ShareNetworkIcon size={24} />
        </Button>
      </div>
      <TeamMemberInfo teamMember={passport.team_member} companyName={passport.company.name} />
      <div className="tw:bg-off-white tw:p-3 tw:space-y-6 tw:min-h-0 tw:flex-1 tw:overflow-y-auto">
        <CertificationSection
          title="Active certifications"
          certifications={activeCertifications}
          emptyLabel="No active certifications"
        />
        <CertificationSection
          title="Expired certifications"
          certifications={expiredCertifications}
          emptyLabel="No expired certifications"
        />
      </div>
      <div className="tw:shrink-0 tw:border-t tw:border-stroke tw:p-3 tw:flex tw:flex-row tw:items-center tw:gap-2 tw:justify-start tw:text-sm tw:text-midnight/50 tw:bg-concrete/25">
        <Scan className="tw:size-4" />
        Scanned on {formatDateString(passport.scanned_at, DATE_FORMAT)}
      </div>
    </PublicSafetyPageContainer>
  );
};

export default PassportPage;
